import { useMemo } from 'react';
import moment from 'moment';
import { useGetListGeneralResults } from '@/hooks/v2/resolution-886/';
import { useResolution886Report } from '@/context/Resolution886Report';

export interface CategoryResult {
	id: string;
	risk: 'TOLERABLE' | 'MODERATE' | 'NOT_TOLERABLE';
	observation: string | null;
	exposure_time: string;
	category: {
		id: string;
		name: string;
		key: string;
	};
}

export interface Review {
	id: string;
	date: string;
	category_results: CategoryResult[];
}

export interface Activity {
	id: string;
	name: string;
	created_at: string;
	reviews: Review[];
}

export interface TableRowData {
	key: string;
	risk_factor: string;
	[reviewKey: string]: string;
}

interface UseGetOverViewRiskFactorsReturn {
	activities: Activity[];
	isLoading: boolean;
}

function findCategoryInReview(review: Review, categoryName: string): CategoryResult | undefined {
	return review.category_results.find((category) => category.category.name === categoryName);
}

export function buildTableData(activity: Activity): TableRowData[] {
	const { reviews } = activity;

	if (!reviews.length) {
		return [];
	}

	const firstReviewCategories = reviews[0].category_results;

	return firstReviewCategories.map((categoryResult, index) => {
		const { category } = categoryResult;

		const rowData: TableRowData = {
			key: `${category.id}-${index}`,
			risk_factor: category.name
		};

		reviews.forEach((review, reviewIndex) => {
			const categoryData = findCategoryInReview(review, category.name);
			rowData[`review_${reviewIndex}`] = categoryData?.risk || '';
		});

		return rowData;
	});
}

export function useGetOverViewRiskFactors(): UseGetOverViewRiskFactorsReturn {
	const locale = moment.locale();
	const { report } = useResolution886Report();

	const { isLoading, data } = useGetListGeneralResults({
		organization_id: report?.organization?.id,
		company_id: report?.company?.id,
		report_id: report?.id,
		locale
	});

	const activities = useMemo((): Activity[] => {
		if (!data?.activities?.length) {
			return [];
		}

		return data.activities;
	}, [data]);

	return {
		isLoading,
		activities
	};
}
